'use client';

import type { UpsertSurfaceProfileDto } from '@terra/shared-contracts-external-network';
import { useTranslations } from 'next-intl';
import { Field } from '../../../components/field';
import { Input } from '../../../components/input';
import { Textarea } from '../../../components/textarea';
import { useDraftOnOpen } from '../../application-general/useDraftOnOpen';
import { SettingsEditDialog } from '../../settings/SettingsEditDialog';
import {
  BUSINESS_PROFILE_TEXT_FIELD,
  toTextFieldUpsert,
  type BusinessProfileTextField,
} from './business-profile.model';

export type BusinessProfileTextFieldDialogProps = {
  field: BusinessProfileTextField | null;
  initialValue: string;
  loading: boolean;
  onClose: () => void;
  onSubmit: (dto: UpsertSurfaceProfileDto) => Promise<boolean>;
};

export function BusinessProfileTextFieldDialog({
  field,
  initialValue,
  loading,
  onClose,
  onSubmit,
}: BusinessProfileTextFieldDialogProps) {
  const t = useTranslations('designSystem.networkSettings.businessProfile');
  const open = field !== null;
  const isDescription = field === BUSINESS_PROFILE_TEXT_FIELD.DESCRIPTION;
  const [draft, setDraft] = useDraftOnOpen({ open, value: initialValue });

  const handleSubmit = async () => {
    if (field === null) {
      return;
    }

    const saved = await onSubmit(toTextFieldUpsert({ field, draft }));

    if (saved) {
      onClose();
    }
  };

  return (
    <SettingsEditDialog
      open={open}
      title={
        isDescription
          ? t('dialogs.editDescription')
          : t('dialogs.editPrimaryIndustry')
      }
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onClose();
        }
      }}
      onSubmit={() => void handleSubmit()}
      loading={loading}
    >
      <Field.Root>
        <Field.Label>
          {isDescription
            ? t('metadata.description')
            : t('metadata.primaryIndustry')}
        </Field.Label>
        {isDescription ? (
          <Textarea
            value={draft}
            rows={4}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={t('metadata.descriptionPlaceholder')}
          />
        ) : (
          <Input
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={t('metadata.primaryIndustryPlaceholder')}
          />
        )}
      </Field.Root>
    </SettingsEditDialog>
  );
}
